import React from 'react';
import { ActivityIndicator, View, Image } from 'react-native';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import { getIntl } from '../../../../../utils/intl';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import useFastenReconnect from '../../../../../hooks/api/useFastenReconnect';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import { useBuildFastenConnectUrl } from '../../../../../hooks/useBuildFastenConnectUrl';
import { openURL } from 'expo-linking';
import { getFastenLogoUrl, formatPlatformName } from '../../../../../utils/fastenUtils';

const intl = getIntl('fasten-reconnect-modal', {
    reconnectTitle: 'Reconnect with Fasten',
    // Reached from both the expired and rejected states, so the wording stays status-neutral.
    reconnectConfirmation: 'Reconnecting opens a secure connection to {name} and restores access to your records.',
    reconnectingMessage: 'Opening a secure connection window...',
});

type ConnectionInfo = {
    brandId?: string;
    institutionName?: string;
    platformType?: string;
} | null;

type UseReconnectFlowArgs = {
    profileId: string;
    orgConnectionId: string;
    autoReconnect?: string;
    connection: ConnectionInfo;
    displayName: string;
    hasReachedLimit?: boolean;
    /** Reconnecting requires edit access to patient portals; without it the server answers 403. */
    hasEditPermission: boolean;
};

export function useReconnectFlow({
    profileId,
    orgConnectionId,
    autoReconnect,
    connection,
    displayName,
    hasReachedLimit,
    hasEditPermission,
}: UseReconnectFlowArgs) {
    const [isReconnectModalOpen, setIsReconnectModalOpen] = React.useState(false);
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const { buildFastenConnectUrl } = useBuildFastenConnectUrl();
    const recordEvent = useRecordEvent();

    const { reconnect, isReconnecting } = useFastenReconnect({
        medicalProfileId: profileId,
        orgConnectionId,
        onReconnectUrlReceived: (redirectUrl) => {
            setPreRedirectArgs(
                {
                    type: 'fasten-connect',
                    profileId,
                    brandId: connection?.brandId || '',
                    brandName: connection?.institutionName || displayName,
                    platformType: connection?.platformType || 'unknown',
                },
                () => {
                    openURL(buildFastenConnectUrl(redirectUrl));
                },
            );
        },
    });

    const handleReconnect = React.useCallback(() => {
        setIsReconnectModalOpen(true);
        recordEvent('fastenPortalReconnect', { profileId, orgConnectionId });
        reconnect();
    }, [reconnect, recordEvent, profileId, orgConnectionId]);

    // Auto-trigger reconnect when navigated with autoReconnect param
    const autoReconnectTriggered = React.useRef(false);
    React.useEffect(() => {
        if (
            autoReconnect === 'true' &&
            connection &&
            !autoReconnectTriggered.current &&
            !hasReachedLimit &&
            hasEditPermission
        ) {
            autoReconnectTriggered.current = true;
            handleReconnect();
        }
    }, [autoReconnect, connection, handleReconnect, hasReachedLimit, hasEditPermission]);

    // Close modal when reconnect finishes
    const reconnectStarted = React.useRef(false);
    React.useEffect(() => {
        if (isReconnecting) {
            reconnectStarted.current = true;
        } else if (reconnectStarted.current && isReconnectModalOpen) {
            reconnectStarted.current = false;
            setIsReconnectModalOpen(false);
        }
    }, [isReconnecting, isReconnectModalOpen]);

    return { handleReconnect, isReconnectModalOpen, setIsReconnectModalOpen, isReconnecting };
}

type ReconnectModalProps = {
    connection: ConnectionInfo;
    displayName: string;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    isReconnecting: boolean;
};

const ReconnectModal: React.FC<ReconnectModalProps> = ({
    connection,
    displayName,
    isOpen,
    setIsOpen,
    isReconnecting,
}) => (
    <StandardPrompt
        isOpen={isOpen}
        setIsOpen={(open) => {
            if (!open && isReconnecting) return;
            setIsOpen(open);
        }}
        title={intl('reconnectTitle')}
    >
        <ContentStack>
            <Stack horizontal align="center" space={3}>
                {connection?.brandId && (
                    <View
                        style={{
                            width: 48,
                            height: 48,
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#f9f9f9',
                            borderRadius: 8,
                        }}
                    >
                        <Image
                            source={{ uri: getFastenLogoUrl(connection.brandId) }}
                            style={{ width: 40, height: 40 }}
                            resizeMode="contain"
                        />
                    </View>
                )}
                <View style={{ flex: 1 }}>
                    <Text variant="h5">{displayName}</Text>
                    {connection?.platformType && (
                        <Text variant="body-2" color="Gray-Text">
                            {formatPlatformName(connection.platformType)}
                        </Text>
                    )}
                </View>
            </Stack>
            <Text variant="body-1">{intl('reconnectConfirmation', { name: displayName })}</Text>
            <Stack horizontal align="center" justify="center" space={2}>
                <ActivityIndicator size="small" color="#4F46E5" />
                <Text variant="body-2" color="Gray-Text">
                    {intl('reconnectingMessage')}
                </Text>
            </Stack>
        </ContentStack>
    </StandardPrompt>
);

export default ReconnectModal;
